/**
 * ScamBlocker AI — Okul Kayıtları (/school)
 * Yalnız org.type==='school' + can_manage_members yetkisi olan staff görür (bkz. Sidebar gating).
 * Öğrenci + veli(ler) toplu kaydı → davet kodu/magic link; enrollment durumu backend'de yaşar.
 * "Koruma aktif" yalnız status==='active' iken gösterilir (PKG-SCHOOL-WEB §C.1/§C.4).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, GraduationCap, Plus, Share2, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '@/features/_shared/page.module.css';
import apiClient from '@/shared/lib/api/client';
import { API } from '@/shared/lib/api/endpoints';
import type {
  CreateStudentsResponse,
  EnrollmentStatus,
  StudentsListResponse,
} from '@/shared/lib/api/types';

interface StudentRowForm {
  /** Kararlı React anahtarı. Dizi indeksi anahtar olduğunda ARADAN bir satır
      silinince React yanlış DOM düğümünü geri kullanıyor ve kullanıcının
      yazdığı değerler bir üst satıra kayıyordu. */
  key: string;
  student_name: string;
  guardian_name: string;
  guardian_phone: string;
  guardian2_name: string;
  guardian2_phone: string;
}

function emptyRow(): StudentRowForm {
  return {
    key: (globalThis.crypto?.randomUUID?.() ?? `row-${Date.now()}-${Math.random()}`),
    student_name: '',
    guardian_name: '',
    guardian_phone: '',
    guardian2_name: '',
    guardian2_phone: '',
  };
}

function rowIsValid(row: StudentRowForm): boolean {
  return (
    row.student_name.trim().length > 0 &&
    row.guardian_name.trim().length > 0 &&
    row.guardian_phone.trim().length > 0
  );
}

interface ApiErrorShape {
  response?: {
    status?: number;
    data?: { error?: string; needed?: number };
  };
}

export function SchoolPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [rows, setRows] = useState<StudentRowForm[]>([emptyRow()]);
  const [result, setResult] = useState<CreateStudentsResponse | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [seatsNeeded, setSeatsNeeded] = useState<number | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery<StudentsListResponse>({
    queryKey: ['school-students'],
    queryFn: async () => (await apiClient.get(API.org.students)).data,
  });

  const createMut = useMutation({
    mutationFn: async () => {
      const payload = {
        students: rows.map((r) => ({
          student_name: r.student_name.trim(),
          guardian_name: r.guardian_name.trim(),
          guardian_phone: r.guardian_phone.trim(),
          ...(r.guardian2_name.trim() ? { guardian2_name: r.guardian2_name.trim() } : {}),
          ...(r.guardian2_phone.trim() ? { guardian2_phone: r.guardian2_phone.trim() } : {}),
        })),
      };
      return (await apiClient.post<CreateStudentsResponse>(API.org.students, payload)).data;
    },
    onSuccess: (resp) => {
      setResult(resp);
      setErrorMsg(null);
      setSeatsNeeded(null);
      setRows([emptyRow()]);
      qc.invalidateQueries({ queryKey: ['school-students'] });
    },
    onError: (e: ApiErrorShape) => {
      setResult(null);
      if (e.response?.status === 402) {
        setSeatsNeeded(e.response.data?.needed ?? null);
        setErrorMsg(t('school.errorSeats'));
      } else {
        setErrorMsg(e.response?.data?.error ?? t('school.errorGeneric'));
      }
    },
  });

  const extraSeatMut = useMutation({
    mutationFn: async () =>
      (await apiClient.post(API.org.extraMemberRequest, { extra_seats: seatsNeeded ?? 1 })).data,
    onSuccess: () => {
      setSeatsNeeded(null);
      window.alert(t('school.extraSeatRequested'));
    },
    onError: (e: ApiErrorShape) =>
      window.alert(
        e.response?.status === 409 ? t('school.extraSeatPending') : t('school.extraSeatError'),
      ),
  });

  const addRow = () => setRows((r) => [...r, emptyRow()]);
  const removeRow = (idx: number) => setRows((r) => r.filter((_, i) => i !== idx));
  const updateRow = (idx: number, field: keyof StudentRowForm, value: string) =>
    setRows((r) => r.map((row, i) => (i === idx ? { ...row, [field]: value } : row)));

  const allValid = rows.length > 0 && rows.every(rowIsValid);

  const copyLink = async (id: string, url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(id);
      window.setTimeout(() => setCopiedId((cur) => (cur === id ? null : cur)), 2000);
    } catch {
      /* clipboard erişilemezse yoksay */
    }
  };

  const shareLink = async (url: string) => {
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ url });
      } catch {
        /* kullanıcı iptal etti veya paylaşım desteklenmiyor */
      }
    }
  };

  const list = data?.students ?? [];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <GraduationCap size={22} className={styles.titleIcon} />
        <h1 className={styles.title}>{t('school.title')}</h1>
        {data && <span className={styles.countPill}>{t('school.studentsCount', { count: list.length })}</span>}
      </div>

      <div className={styles.tableWrapper} style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <strong>{t('school.formTitle')}</strong>
        {rows.map((row, idx) => (
          <div className={styles.formRow} key={row.key}>
            <div className={styles.field}>
              <label htmlFor={`student-name-${idx}`}>{t('school.studentNameLabel')}</label>
              <input
                id={`student-name-${idx}`}
                className={styles.input}
                placeholder={t('school.studentNamePlaceholder')}
                value={row.student_name}
                onChange={(e) => updateRow(idx, 'student_name', e.target.value)}
              />
            </div>
            <div className={styles.field}>
              <label htmlFor={`guardian-name-${idx}`}>{t('school.guardianNameLabel')}</label>
              <input
                id={`guardian-name-${idx}`}
                className={styles.input}
                placeholder={t('school.guardianNamePlaceholder')}
                value={row.guardian_name}
                onChange={(e) => updateRow(idx, 'guardian_name', e.target.value)}
              />
            </div>
            <div className={styles.field}>
              <label htmlFor={`guardian-phone-${idx}`}>{t('school.guardianPhoneLabel')}</label>
              <input
                id={`guardian-phone-${idx}`}
                className={styles.input}
                placeholder={t('school.guardianPhonePlaceholder')}
                value={row.guardian_phone}
                onChange={(e) => updateRow(idx, 'guardian_phone', e.target.value)}
              />
            </div>
            <div className={styles.field}>
              <label htmlFor={`guardian2-name-${idx}`}>{t('school.guardian2NameLabel')}</label>
              <input
                id={`guardian2-name-${idx}`}
                className={styles.input}
                value={row.guardian2_name}
                onChange={(e) => updateRow(idx, 'guardian2_name', e.target.value)}
              />
            </div>
            <div className={styles.field}>
              <label htmlFor={`guardian2-phone-${idx}`}>{t('school.guardian2PhoneLabel')}</label>
              <input
                id={`guardian2-phone-${idx}`}
                className={styles.input}
                value={row.guardian2_phone}
                onChange={(e) => updateRow(idx, 'guardian2_phone', e.target.value)}
              />
            </div>
            {rows.length > 1 && (
              <button
                type="button"
                className={`${styles.actionBtn} ${styles.dangerBtn}`}
                aria-label={t('school.removeRow')}
                onClick={() => removeRow(idx)}
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        ))}

        <div className={styles.formRow}>
          <button type="button" className={styles.actionBtn} onClick={addRow}>
            <Plus size={14} /> {t('school.addRow')}
          </button>
          <button
            type="button"
            className={styles.actionBtn}
            disabled={!allValid || createMut.isPending}
            onClick={() => createMut.mutate()}
          >
            {createMut.isPending ? t('school.submitting') : t('school.submit')}
          </button>
        </div>
      </div>

      {errorMsg && (
        <div role="alert" className={styles.errorBox}>
          {errorMsg}
          {seatsNeeded != null && (
            <>
              {' '}
              <button
                type="button"
                className={styles.actionBtn}
                disabled={extraSeatMut.isPending}
                onClick={() => extraSeatMut.mutate()}
              >
                {t('school.requestExtraSeats')}
              </button>
            </>
          )}
        </div>
      )}

      {result && result.enrollments.length > 0 && (
        <div className={styles.tableWrapper} style={{ padding: '1rem' }}>
          <strong>{t('school.resultTitle')}</strong>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('school.colInviteCode')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {result.enrollments.map((e) => (
                <tr key={e.id} className={styles.row}>
                  <td className={styles.mono}>{e.invite_code}</td>
                  <td>
                    <button
                      type="button"
                      className={styles.actionBtn}
                      onClick={() => copyLink(e.id, e.invite_url)}
                    >
                      <Copy size={14} /> {copiedId === e.id ? t('school.copied') : t('school.copyLink')}
                    </button>
                    {typeof navigator !== 'undefined' && typeof navigator.share === 'function' && (
                      <button
                        type="button"
                        className={styles.actionBtn}
                        onClick={() => shareLink(e.invite_url)}
                      >
                        <Share2 size={14} /> {t('school.share')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className={styles.tableWrapper}>
        <div style={{ padding: '0.7rem 0.9rem' }}>
          <strong>{t('school.existingTitle')}</strong>
        </div>
        {isLoading && (
          <div className={styles.center}>
            <span className={styles.spinner} />
          </div>
        )}
        {isError && <div className={styles.errorBox}>{t('school.loadError')}</div>}
        {!isLoading && !isError && (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('school.colStudent')}</th>
                <th>{t('school.colStatus')}</th>
                <th>{t('school.colProtection')}</th>
              </tr>
            </thead>
            <tbody>
              {list.map((s) => (
                <tr key={s.id} className={styles.row}>
                  <td>{s.student_name}</td>
                  <td>{t(`school.status.${s.status as EnrollmentStatus}`)}</td>
                  <td>
                    {s.status === 'active' && (
                      <span className={styles.badge} style={{ color: 'var(--color-success, #2ecc71)' }}>
                        {t('school.activeLabel')}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {list.length === 0 && (
                <tr>
                  <td colSpan={3} className={styles.empty}>
                    {t('school.emptyState')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
